import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { useAuth } from "./context/AuthContext";
import { CartProvider, useCart } from "./context/CartContext";

import LandingPage from "./pages/LandingPage";
import SignupPage from "./pages/craftsman/SignupPage";
import VerifyEmail from "./pages/craftsman/VerifyEmail";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import LoginPage from "./pages/craftsman/LoginPage";
import DashboardPage from "./pages/craftsman/DashboardPage";
import PublicProfile from "./pages/PublicProfile";
import Navbar from "./components/Navbar";
import CartPage from "./pages/CartPage";

import WoodworkingCategoryPage from "./pages/WoodworkingCategoryPage";
import MetalworkingCategoryPage from "./pages/MetalworkingCategoryPage";
import TextileCategoryPage from "./pages/TextileCategoryPage";

import "bootstrap/dist/css/bootstrap.min.css";

import CraftsmenListPage from "./pages/craftsman/CraftsmenListPage";
import CraftsmanProfilePage from "./pages/craftsman/CraftsmanProfilePage";

import HireSignUp from "./pages/client/HireSignUpPage";
import HireCraftsmanPage from "./pages/client/HireCraftsmanPage";
import HireLogin from "./pages/client/HireLoginPage";

import TermsConditionPage from "./pages/TermsConditionPage";
import ServicesPage from "./pages/ServicesPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProtectedRoute from "./components/ProtectedRoute";
import NoShopPage from "./pages/NoShopPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import Onboarding from "./pages/craftsman/Onboarding";

import Jobs from "./pages/Jobs/JobsPage";
import JobDetails from "./pages/Jobs/JobsDetailsPage";
import ReportIssuePage from "./pages/ReportIssuePage";

// ── Shop ──────────────────────────────────────────────────────────────────────
import ShopPage from "./pages/shop/ShopPage";
import ProductDetailPage from "./pages/shop/ProductDetailPage";
import CheckoutPage from "./pages/shop/CheckoutPage";

// ── Cookie & AI Assistant ─────────────────────────────────────────────────────
import CookieConsentBanner from "./components/CookieConsentBanner";
// import AIAssistantWidget from "./components/AIAssistantWidget";


// ─────────────────────────────────────────────────────────────────────────────
// NAVBAR
// ─────────────────────────────────────────────────────────────────────────────

function ConditionalNavbar() {
  const location = useLocation();
  const { totalItems } = useCart();


  const isShopPage =
    location.pathname === "/shop" ||
    location.pathname.startsWith("/product/") ||
    location.pathname === "/checkout";

  if (isShopPage) {
    return null;
  }

  return <Navbar cart={totalItems} />;
}


// ─────────────────────────────────────────────────────────────────────────────
// ROOT ROUTE
// ─────────────────────────────────────────────────────────────────────────────

function RootRoute() {
  const { user, loading } = useAuth();
  const { items, addToCart, updateQty } = useCart();

  if (loading) {
    return null;
  }

  if (user) {
    if (user.role === "craftsman") {
      return <Navigate to="/craftsman-dashboard" replace />;
    }

    if (user.role === "client") {
      return <Navigate to="/hire" replace />;
    }

    if (user.role === "admin") {
      return <Navigate to="/admin-dashboard" replace />;
    }
  }

  /*
   * LandingPage may still use the old cart props.
   * We provide compatible values from the new CartContext.
   */
  const cart = items.reduce((acc, item) => {
    acc[item.title] = item.qty;
    return acc;
  }, {});

  const setCart = () => {
    // Kept only for compatibility with LandingPage.
    // The shop itself uses CartContext.
  };

  const incrementQuantity = (productName) => {
    const item = items.find((item) => item.title === productName);

    if (item) {
      updateQty(item.cartItemId, item.qty + 1);
    }
  };

  const decrementQuantity = (productName) => {
    const item = items.find((item) => item.title === productName);

    if (item) {
      updateQty(item.cartItemId, item.qty - 1);
    }
  };

  return (
    <LandingPage
      cart={cart}
      setCart={setCart}
      incrementQuantity={incrementQuantity}
      decrementQuantity={decrementQuantity}
    />
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// APP CONTENT
// ─────────────────────────────────────────────────────────────────────────────

function AppContent() {
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);

  return (
    <Router>
      <ConditionalNavbar />

      {/* AI Assistant
      <AIAssistantWidget
        cookieBannerVisible={cookieBannerVisible}
      /> */}

      <Routes>

        {/* ──────────────────────────────────────────────────────────────── */}
        {/* ROOT */}
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route
          path="/"
          element={<RootRoute />}
        />


        {/* ──────────────────────────────────────────────────────────────── */}
        {/* SHOP */}
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route
          path="/shop"
          element={<ShopPage />}
        />

        <Route
          path="/product/:id"
          element={<ProductDetailPage />}
        />

        <Route
          path="/checkout"
          element={<CheckoutPage />}
        />


        {/* ──────────────────────────────────────────────────────────────── */}
        {/* PUBLIC */}
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route
          path="/signup"
          element={<SignupPage />}
        />

        <Route
          path="/login"
          element={<LoginPage />}
        />

        <Route
          path="/verify-email"
          element={<VerifyEmail />}
        />

        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage />}
        />

        {/* ✅ FIXED: now matches the email link shape /reset-password/<uid>/<token>/ */}
        <Route
          path="/reset-password/:uid/:token"
          element={<ResetPasswordPage />}
        />

        <Route
          path="/profiles/:username"
          element={<PublicProfile />}
        />

        <Route
          path="/cart"
          element={<CartPage />}
        />

        <Route
          path="/category/woodworking"
          element={<WoodworkingCategoryPage />}
        />

        <Route
          path="/category/metalworking"
          element={<MetalworkingCategoryPage />}
        />

        <Route
          path="/category/textile"
          element={<TextileCategoryPage />}
        />

        <Route
          path="/craftsmen"
          element={<CraftsmenListPage />}
        />

        <Route
          path="/craftsman/:slug"
          element={<CraftsmanProfilePage />}
        />

        <Route
          path="/craftsmen/:slug"
          element={<CraftsmanProfilePage />}
        />

        <Route
          path="/HireSignUp"
          element={<HireSignUp />}
        />

        <Route
          path="/HireLogin"
          element={<HireLogin />}
        />

        <Route
          path="/terms"
          element={<TermsConditionPage />}
        />

        <Route
          path="/services"
          element={<ServicesPage />}
        />

        <Route
          path="/no-shop"
          element={<NoShopPage />}
        />

        <Route
          path="/orders"
          element={<OrderTrackingPage />}
        />

        <Route
          path="/onboarding"
          element={<Onboarding />}
        />

        <Route
          path="/report-issue"
          element={<ReportIssuePage />}
        />


        {/* ──────────────────────────────────────────────────────────────── */}
        {/* JOBS */}
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route
          path="/jobs"
          element={<Jobs />}
        />

        <Route
          path="/jobs/:id"
          element={<JobDetails />}
        />


        {/* ──────────────────────────────────────────────────────────────── */}
        {/* CLIENT PROTECTED */}
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route
        path="/hire/*"
        element={
            <ProtectedRoute role="client">
              <HireCraftsmanPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute role="client">
            </ProtectedRoute>
          }
        />


        {/* ──────────────────────────────────────────────────────────────── */}
        {/* CRAFTSMAN PROTECTED */}
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route
          path="/craftsman-dashboard"
          element={
            <ProtectedRoute role="craftsman">
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute role="craftsman">
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/craftsman/profile"
          element={
            <ProtectedRoute role="craftsman">
              <CraftsmanProfilePage />
            </ProtectedRoute>
          }
        />

      </Routes>

      {/* Cookie Consent */}
      <CookieConsentBanner
        onVisibleChange={setCookieBannerVisible}
      />
    </Router>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <CartProvider>
      <AppContent />
    </CartProvider>
  );
}